import { AlertTriangle, Check, X } from "lucide-react";

import type { DraftBatchItem } from "@/api/types";
import { EntryDetail } from "@/components/catalogue/EntryDetail";
import { viewFromBatchItem } from "@/components/catalogue/entryDetailAdapter";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

/**
 * One row in the review panel. Renders every field the reviewer needs to
 * make a decision (curator's confidence/source counts on the left,
 * validator's quality data on the right) plus a pair of approve/reject
 * controls. A flagged row gets an amber border + warning label so it
 * stands out at a glance.
 *
 * The presentational body — the two-column field grid + embedding-text
 * section + raw-JSON toggle — lives in `<EntryDetail>` and is shared
 * with the catalogue and drafts expand-on-click views (issue #218).
 * `ReviewRow` only owns the flagged-amber wrapper, the header with
 * approve/reject controls, and the "locked" decided-badge state.
 *
 * When `item.existingDecision` is non-null this row was decided in a
 * prior submit — controls hide and a "Decided <approved|rejected>"
 * badge shows the prior outcome. The audit_log invariant (append-only)
 * means this is permanent within a job; the panel cannot un-decide a
 * row.
 */
export interface ReviewRowProps {
  item: DraftBatchItem;
  /** In-session decision (set by clicking approve/reject; null until clicked). */
  decision: "approved" | "rejected" | null;
  /** Called when the reviewer clicks an approve/reject button. */
  onDecide: (decision: "approved" | "rejected") => void;
}

export function ReviewRow({ item, decision, onDecide }: ReviewRowProps) {
  const flagged = item.validatorFlag === true;
  const locked = item.existingDecision !== null;

  return (
    <Card
      className={cn(
        flagged && "border-amber-500/50 bg-amber-500/5",
        locked && "opacity-70",
      )}
    >
      <CardContent className="space-y-3 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-display text-lg font-medium text-text">{item.name}</h3>
              {flagged ? (
                <Badge
                  variant="default"
                  className="border-amber-500/40 bg-amber-500/10 text-amber-300"
                >
                  <AlertTriangle className="mr-1 h-3 w-3" />
                  flagged
                </Badge>
              ) : null}
            </div>
            <p className="text-sm text-text-muted">
              {item.vendor}{" "}
              <a
                href={item.url}
                target="_blank"
                rel="noreferrer"
                className="text-accent hover:underline"
              >
                {item.url}
              </a>
            </p>
          </div>
          {locked ? (
            <Badge
              variant={
                item.existingDecision === "approved"
                  ? item.existingDecisionPromoted === false
                    ? "default"
                    : "success"
                  : "danger"
              }
              className={
                item.existingDecision === "approved" &&
                item.existingDecisionPromoted === false
                  ? "border-amber-500/40 bg-amber-500/10 text-amber-300"
                  : undefined
              }
            >
              decided · {item.existingDecision}
              {item.existingDecision === "approved"
                ? item.existingDecisionPromoted === true
                  ? " · promoted"
                  : item.existingDecisionPromoted === false
                    ? " · pending"
                    : ""
                : ""}
            </Badge>
          ) : null}
        </div>

        <EntryDetail view={viewFromBatchItem(item)} raw={item} />

        {!locked ? (
          <div className="flex justify-end gap-2 pt-1">
            <Button
              variant={decision === "rejected" ? "destructive" : "outline"}
              size="sm"
              aria-pressed={decision === "rejected"}
              onClick={() => onDecide("rejected")}
            >
              <X className="mr-1 h-3.5 w-3.5" />
              Reject
            </Button>
            <Button
              variant={decision === "approved" ? "default" : "outline"}
              size="sm"
              aria-pressed={decision === "approved"}
              onClick={() => onDecide("approved")}
            >
              <Check className="mr-1 h-3.5 w-3.5" />
              Approve
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
